import {
  InPlaceComboBoxEditor,
  InPlaceDatePickerEditor,
  InPlaceDropDownEditor
} from "@etframework/ui.components";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { useUpdateTask } from "../../data/mutations/taskMutations";
import { useAgencies } from "../../data/queries/agencyQueries";
import { useTaxYears } from "../../data/queries/taxReturnQueries";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";
import useEvent from "../../hooks/useEvent";
import { DateFormats } from "../../types/DateFormats";
import Enums from "../../types/enums";
import { formatDate } from "../../utils/helpers";
import { DateEditor } from "./DateEditor";
import { ExtensionContainer } from "./ExtensionContainer";
import { FilingStatusDropdown } from "./FilingStatusDropdown";
import { FormTaskTaxReturnDetail } from "./FormTaskTaxReturnDetail";
import { TaskDetailTaxReturn } from "./TaskDetailTaxReturn";

export const TaxReturnInformation = ({
  ids,
  canModifyTasks,
  task,
  visualStatusRef
}) => {
  const { UserId } = useDueDatesAuth();
  const { t, i18n } = useTranslation(["Overlay", "Shared"], {
    useSuspense: false
  });

  const taxDeadlineRef = useRef(null);
  const periodEndRef = useRef(null);
  const agencyRef = useRef(null);

  //Mutations
  const { mutate: updateTask } = useUpdateTask();
  const queryClient = useQueryClient();
  const { Events, toggleEvent } = useEvent();
  const { data: taxYears } = useTaxYears();
  const { data: agencies } = useAgencies();

  const getTaxYears = () => {
    //add the year value to the initial value if it doesn't exist in the taxYears list
    const years = taxYears?.map((y) => y.Year) ?? [];
    if (
      task?.TaskTaxReturnInfo?.TaxYear &&
      !(taxYears ?? []).some(
        (y) => y.Year === Number(task.TaskTaxReturnInfo.TaxYear)
      )
    ) {
      years.push(Number(task.TaskTaxReturnInfo.TaxYear));
    }

    return years;
  };

  const onTaskModified = (e) => {
    let modifiedTask = {
      ...task,
      TemplateId: task?.Template?.Id,
      CurrentUserId: UserId,
      Collaborators: null,
      Tags: null,
      AssigneeId: task?.Assignee?.Id
    };

    if (e?.id) {
      // this triggers on `actionSuccess` event of InPlaceEditors
      const value = e.value;
      switch (e.id) {
        case ids.taxReturnInfo.template: {
          if (value == task?.Template?.Id || (!value && !task?.Template?.Id))
            return;
          modifiedTask = {
            ...modifiedTask,
            TemplateId: value
          };
          break;
        }
        case ids.taxReturnInfo.taxYear:
          if (value == task?.TaskTaxReturnInfo?.TaxYear) return;
          modifiedTask = {
            ...modifiedTask,
            TaskTaxReturnInfo: {
              ...modifiedTask?.TaskTaxReturnInfo,
              TaxYear: value
            }
          };
          break;
        case ids.taxReturnInfo.taxDeadline:
          if (
            formatDate(
              value,
              DateFormats.ShortDateFormatHyphen.momentJsFormat
            ) ==
            formatDate(
              task?.TaskTaxReturnInfo?.TaxDeadline,
              DateFormats.ShortDateFormatHyphen.momentJsFormat
            )
          )
            return;
          modifiedTask = {
            ...modifiedTask,
            TaskTaxReturnInfo: {
              ...modifiedTask.TaskTaxReturnInfo,
              TaxDeadline: value
            }
          };
          break;
        case ids.taxReturnInfo.extendedTaxDeadline:
          if (
            formatDate(
              value,
              DateFormats.ShortDateFormatHyphen.momentJsFormat
            ) ==
            formatDate(
              task?.TaskTaxReturnInfo?.ExtendedTaxDeadline,
              DateFormats.ShortDateFormatHyphen.momentJsFormat
            )
          )
            return;
          modifiedTask = {
            ...modifiedTask,
            TaskTaxReturnInfo: {
              ...modifiedTask.TaskTaxReturnInfo,
              ExtendedTaxDeadline: value
            }
          };
          break;
        case ids.taxReturnInfo.periodEnd:
          if (
            formatDate(
              value,
              DateFormats.ShortDateFormatHyphen.momentJsFormat
            ) ==
            formatDate(
              task?.TaskTaxReturnInfo?.PeriodEnd,
              DateFormats.ShortDateFormatHyphen.momentJsFormat
            )
          )
            return;
          modifiedTask = {
            ...modifiedTask,
            TaskTaxReturnInfo: {
              ...modifiedTask.TaskTaxReturnInfo,
              PeriodEnd: value
            }
          };
          break;
        case ids.taxReturnInfo.filingStatus:
          if (value == task?.TaskTaxReturnInfo.FilingStatus) return;
          modifiedTask = {
            ...modifiedTask,
            TaskTaxReturnInfo: {
              ...modifiedTask?.TaskTaxReturnInfo,
              FilingStatus: value
            }
          };
          break;
        case ids.taxReturnInfo.agency:
          if (value?.Name == task?.TaskTaxReturnInfo?.Agency?.Name) return;

          let agency =
            value?.Id === value?.Name ? { Name: value?.Name } : value;

          modifiedTask = {
            ...modifiedTask,
            TaskTaxReturnInfo: {
              ...modifiedTask?.TaskTaxReturnInfo,
              Agency: agency
            }
          };
          break;
        default:
          break;
      }
    }

    updateTask(
      { task: modifiedTask },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["get-task", task?.Id]);

          if (
            task?.TaskTaxReturnInfo?.Agency?.Id !==
            modifiedTask?.TaskTaxReturnInfo?.Agency?.Id
          )
            queryClient.invalidateQueries(["get-agencies"]);

          toggleEvent(Events.RefreshTaskGrid);
          toast.success(t("Success"), { position: "bottom-left" });
        },
        onError: () => {
          toast.error(t("ErrorMsg"), { position: "bottom-left" });
        }
      }
    );
  };

  return (
    <div className="tax-return-container bc-bg-light-gray br-4">
      <div className="d-flex justify-content-between align-items-center mx-3 my-1">
        <div className="d-flex gap-2">
          <div className="d-flex align-items-center">
            <i className="fa-kit fa-tax-return fa-1x et-darkest-gray"></i>
          </div>
          {task.Template != null ? (
            <div className="fw-600 fs-14 et-black">
              {task?.TaskTaxReturnInfo?.FormCode} {t("ForTaxYear")}
              {task?.TaskTaxReturnInfo?.TaxYear}
            </div>
          ) : (
            <div className="fw-600 fs-14 et-black">
              {t("EmptyTaskTaxReturnInfo", {
                taxYear: task?.TaskTaxReturnInfo?.TaxYear
              })}
            </div>
          )}
        </div>

        <FilingStatusDropdown
          id={ids.taxReturnInfo.filingStatus}
          value={task?.TaskTaxReturnInfo?.FilingStatus}
          disabled={!canModifyTasks}
          change={onTaskModified}
        />
      </div>
      <div className="et-bt-light-gray-1 d-flex justify-content-between align-items-start">
        <div className="d-flex flex-grow-1 flex-column justify-content-between align-items-start mx-3 my-2">
          <FormTaskTaxReturnDetail
            ids={ids}
            onTaskModified={onTaskModified}
            canModifyTasks={canModifyTasks}
            taskTypeId={task?.TaskType?.Id}
            taskTemplate={task.Template}
          />
          <TaskDetailTaxReturn label={t("Agency")}>
            <InPlaceComboBoxEditor
              ref={agencyRef}
              id={ids.taxReturnInfo.agency}
              /*
               * This avoids the combobox from entering an unbreakable loading state when the value
               * is not in the datasource (bug#141856, 12/2025)
               */
              // dataSource={
              //   Array.isArray(agencies) &&
              //   !agencies.find(
              //     (x) => x.Id === task?.TaskTaxReturnInfo?.Agency?.Id
              //   ) &&
              //   task?.TaskTaxReturnInfo?.Agency
              //     ? [...agencies, task?.TaskTaxReturnInfo?.Agency]
              //     : agencies
              // }
              dataSource={agencies}
              fields={{ text: "Name", value: "Id" }}
              value={task?.TaskTaxReturnInfo?.Agency}
              allowObjectBinding
              allowFiltering
              disabled={!canModifyTasks}
              submitClick={(e) => {
                e.id = ids.taxReturnInfo.agency;
                onTaskModified(e);
              }}
            />
          </TaskDetailTaxReturn>
          <TaskDetailTaxReturn label={t("TaxYear")}>
            <InPlaceDropDownEditor
              id={ids.taxReturnInfo.taxYear}
              dataSource={getTaxYears()}
              value={Number(task?.TaskTaxReturnInfo?.TaxYear)}
              sortOrder="Descending"
              model={{ showClearButton: false }}
              disabled={!canModifyTasks}
              actionSuccess={(e) => {
                e.id = ids.taxReturnInfo.taxYear;
                onTaskModified(e);
              }}
            />
          </TaskDetailTaxReturn>
          <TaskDetailTaxReturn label={t("PeriodEnd")}>
            <InPlaceDatePickerEditor
              ref={periodEndRef}
              id={ids.taxReturnInfo.periodEnd}
              submitClick={(e) => {
                e.id = ids.taxReturnInfo.periodEnd;
                e.value = periodEndRef.current?.getValue();
                onTaskModified(e);
              }}
              model={{ showClearButton: false }}
              value={moment.utc(task?.TaskTaxReturnInfo?.PeriodEnd).toDate()}
              disabled={!canModifyTasks}
              language={i18n.language}
              actionOnBlur={"Cancel"}
            />
          </TaskDetailTaxReturn>
          <TaskDetailTaxReturn
            label={
              task?.TaskTaxReturnInfo?.ExtendedTaxDeadline == null
                ? t("TaxDeadline")
                : t("ExtendedTaxDeadline")
            }
          >
            <DateEditor
              ref={taxDeadlineRef}
              id={
                task?.TaskTaxReturnInfo?.ExtendedTaxDeadline
                  ? ids.taxReturnInfo.extendedTaxDeadline
                  : ids.taxReturnInfo.taxDeadline
              }
              //isTaxReturn={task?.TaskType?.Type == Enums.TaskType.TaxReturn}
              value={task?.TaskTaxReturnInfo?.EffectiveTaxDeadline}
              task={task}
              inlineActionSuccess={(e) => {
                e.id = task?.TaskTaxReturnInfo?.ExtendedTaxDeadline
                  ? ids.taxReturnInfo.extendedTaxDeadline
                  : ids.taxReturnInfo.taxDeadline;
                e.value = taxDeadlineRef.current?.getValue();
                onTaskModified(e);
              }}
              //onClickCalendar={postExtendedDate}
              canModifyTasks={canModifyTasks}
              //visualStatusRef={visualStatusRef}
            />
          </TaskDetailTaxReturn>
        </div>

        <ExtensionContainer
          isTaxReturn={task?.TaskType?.Type == Enums.TaskType.TaxReturn}
          visualStatusRef={visualStatusRef}
          task={task}
          canModifyTasks={canModifyTasks}
        />
      </div>
    </div>
  );
};
